import React, { useCallback, useEffect, useRef } from 'react';
import { Paper, Typography, TextField, Grid } from '@mui/material';
import DynamicDatePicker from '../../../../common/DynamicDatePicker';
import { SiteConditionsDTO } from '../../types/monitoring-report.types';

const todayLocal = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
const CURRENT_YEAR_REF = new Date(new Date().getFullYear(), 0, 1);

interface SiteConditionsSectionProps {
  siteConditions: SiteConditionsDTO;
  onChange: (siteConditions: SiteConditionsDTO) => void;
}

const SiteConditionsSection: React.FC<SiteConditionsSectionProps> = ({ siteConditions, onChange }) => {
  const onChangeRef = useRef(onChange);
  const siteConditionsRef = useRef(siteConditions);

  // Keep refs in sync with latest props so stale closures don't fire with old values
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    siteConditionsRef.current = siteConditions;
  }, [siteConditions]);

  // Default reportDate and inspectionDate to today's local date on mount, only for dates that aren't set yet
  useEffect(() => {
    const current = siteConditionsRef.current;
    const today = todayLocal();
    const defaults: Partial<SiteConditionsDTO> = {};
    if (!current.reportDate) defaults.reportDate = today;
    if (!current.inspectionDate) defaults.inspectionDate = today;
    if (Object.keys(defaults).length > 0) {
      onChangeRef.current({ ...current, ...defaults });
    }
  }, []);

  const handleFieldChange = useCallback(
    (field: keyof SiteConditionsDTO, value: string) => {
      onChange({
        ...siteConditions,
        [field]: value,
      });
    },
    [siteConditions, onChange],
  );

  const handleDateChange = useCallback(
    (field: string, value: string | null) => {
      handleFieldChange(field as keyof SiteConditionsDTO, value ?? '');
    },
    [handleFieldChange],
  );

  return (
    <Paper sx={{ p: 3, mb: 3, backgroundColor: 'white' }}>
      <Typography variant='h5' sx={{ mb: 2 }}>
        Site Conditions
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            fullWidth
            label='Site Visit Number'
            value={siteConditions.siteVisit || ''}
            // Whole numbers only, since Start Next Report increments it
            onChange={e => handleFieldChange('siteVisit', e.target.value.replace(/\D/g, ''))}
            slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' } }}
            variant='outlined'
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <DynamicDatePicker
            fieldName='inspectionDate'
            label='Inspection Date'
            value={siteConditions.inspectionDate || null}
            referenceDate={CURRENT_YEAR_REF}
            onChange={handleDateChange}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            fullWidth
            label='Report Number'
            value={siteConditions.reportNumber || ''}
            // Whole numbers only: drop anything that isn't a digit (type='number' still allows e, ., -, +)
            onChange={e => handleFieldChange('reportNumber', e.target.value.replace(/\D/g, ''))}
            slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' } }}
            variant='outlined'
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <DynamicDatePicker
            fieldName='reportDate'
            label='Report Date'
            value={siteConditions.reportDate || null}
            onChange={handleDateChange}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SiteConditionsSection;
